import { useCallback, useEffect, useRef, useState } from "react";

import type { InsuranceCardSide } from "src/api/types";
import { prepareImage } from "src/lib/resizeImage";

const DB_NAME = "walkin";
const STORE_NAME = "insurance_card_drafts";

export type Side = InsuranceCardSide;

export interface Draft {
  front: Blob | null;
  back: Blob | null;
}

export interface UseInsuranceCardDraftResult {
  draft: Draft;
  loading: boolean;
  processing: Side | null;
  setImage: (side: Side, file: File) => Promise<void>;
  clear: () => Promise<void>;
}

const EMPTY: Draft = { front: null, back: null };

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexeddb_unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

export function useInsuranceCardDraft(clientId: string): UseInsuranceCardDraftResult {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Side | null>(null);
  const draftRef = useRef<Draft>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    draftRef.current = EMPTY;
    // Reset to an empty draft when the client changes, before loading the stored one.
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setDraft(EMPTY);
    setLoading(true);
    withStore<Draft | undefined>("readonly", (s) => s.get(clientId))
      .then((stored) => {
        if (cancelled || !stored) return;
        // A photo chosen before the read finished wins over the stored one.
        const merged = {
          front: draftRef.current.front ?? stored.front ?? null,
          back: draftRef.current.back ?? stored.back ?? null,
        };
        draftRef.current = merged;
        setDraft(merged);
      })
      .catch(() => {
        // IndexedDB unavailable: in-memory only.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const setImage = useCallback(
    async (side: Side, file: File) => {
      setProcessing(side);
      let blob: Blob;
      try {
        blob = await prepareImage(file);
      } finally {
        setProcessing(null);
      }
      const next = { ...draftRef.current, [side]: blob };
      draftRef.current = next;
      setDraft(next);
      await withStore("readwrite", (s) => s.put(next, clientId)).catch(() => {});
    },
    [clientId],
  );

  const clear = useCallback(async () => {
    draftRef.current = EMPTY;
    setDraft(EMPTY);
    await withStore("readwrite", (s) => s.delete(clientId)).catch(() => {});
  }, [clientId]);

  return { draft, loading, processing, setImage, clear };
}
